<script setup>
import { formatDate } from './helpers';
import { superdocIcons } from '@superdoc/icons.js';
import { computed, getCurrentInstance } from 'vue';
import { actorIdentitiesMatch, getActorIdentity, normalizeActorName } from '@superdoc/common';
import { isAllowed, PERMISSIONS } from '@superdoc/core/collaboration/permissions.js';
import { useCommentsStore } from '@superdoc/stores/comments-store';
import Avatar from '@superdoc/components/general/Avatar.vue';
import { useUiFontFamily } from '@superdoc/composables/useUiFontFamily.js';
import CommentsDropdown from './CommentsDropdown.vue';

const emit = defineEmits(['resolve', 'reject', 'overflow-select']);
const commentsStore = useCommentsStore();
const props = defineProps({
  timestamp: {
    type: Number,
    required: false,
  },
  config: {
    type: Object,
    required: true,
  },
  comment: {
    type: Object,
    required: false,
  },
  isPendingInput: {
    type: Boolean,
    required: false,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  // Stable reason for disabling resolve / reject. When set, the buttons render
  // in a disabled state and emit nothing.
  resolveDisabledReason: {
    type: String,
    default: null,
  },
  rejectDisabledReason: {
    type: String,
    default: null,
  },
});

const { proxy } = getCurrentInstance();
const role = proxy.$superdoc.config.role;
const isInternal = proxy.$superdoc.config.isInternal;
const isCommentOwnedByCurrentUser = (comment) => {
  const currentUser = proxy.$superdoc.config.user;
  const otherUser = { id: comment?.creatorId, email: comment?.creatorEmail };
  if (actorIdentitiesMatch({ current: currentUser, other: otherUser })) return true;

  const currentIdentity = getActorIdentity(currentUser);
  const otherIdentity = getActorIdentity(otherUser);
  if (currentIdentity.hasId || currentIdentity.hasEmail || otherIdentity.hasId || otherIdentity.hasEmail) {
    return false;
  }

  const hasImportOrigin = comment?.origin != null || Boolean(comment?.importedAuthor?.name);
  if (hasImportOrigin) return false;

  const currentName = normalizeActorName(currentUser?.name);
  const commentName = normalizeActorName(comment?.creatorName);
  return Boolean(currentName && commentName && currentName === commentName);
};
const hasImportedAuthorConflict = (comment, currentUser) => {
  const importedAuthorName = normalizeActorName(comment?.importedAuthor?.name).replace(/\s+\(imported\)$/, '');
  const currentName = normalizeActorName(currentUser?.name);
  const changeName = normalizeActorName(comment?.creatorName);

  if (
    !importedAuthorName ||
    importedAuthorName === 'undefined' ||
    importedAuthorName === 'null' ||
    !currentName ||
    !changeName
  ) {
    return false;
  }
  return importedAuthorName !== currentName && changeName !== currentName;
};

// Accept/Reject must use the same ownership rules as `classifyOwnership` in
// editor-core tracked-change identity. Imported-author conflicts take priority
// over otherwise matching ids or emails.
const isTrackedChangeOwnedByCurrentUser = (comment) => {
  const currentUser = proxy.$superdoc.config.user;
  if (hasImportedAuthorConflict(comment, currentUser)) return false;
  return actorIdentitiesMatch({
    current: currentUser,
    other: { id: comment?.creatorId, email: comment?.creatorEmail },
  });
};
const isOwnComment = computed(() => {
  if (props.comment?.trackedChange) return isTrackedChangeOwnedByCurrentUser(props.comment);
  return isCommentOwnedByCurrentUser(props.comment);
});

const { uiFontFamily } = useUiFontFamily();

const OVERFLOW_OPTIONS = Object.freeze({
  edit: { label: 'Edit', key: 'edit' },
  delete: { label: 'Delete', key: 'delete' },
});

const generallyAllowed = computed(() => {
  if (!props.comment) return false;
  if (props.comment.resolvedTime) return false;
  if (commentsStore.pendingComment) return false;
  if (props.isPendingInput) return false;
  return true;
});

const allowResolve = computed(() => {
  if (!generallyAllowed.value) return false;

  // Do not allow child comments to resolve. A reply anchored to a tracked
  // change keeps the linkage via trackedChangeParentId (no parentCommentId),
  // so treat it as a child too — otherwise re-imported TC replies render an
  // extra resolve affordance that the live pre-export state doesn't (SD-2528).
  if (props.comment.parentCommentId) return false;
  if (props.comment.trackedChangeParentId) return false;

  const context = {
    comment: props.comment,
    currentUser: proxy.$superdoc.config.user,
    superdoc: proxy.$superdoc,
  };

  if (isOwnComment.value) {
    return isAllowed(PERMISSIONS.RESOLVE_OWN, role, isInternal, context);
  }
  return isAllowed(PERMISSIONS.RESOLVE_OTHER, role, isInternal, context);
});

const allowReject = computed(() => {
  if (!generallyAllowed.value) return false;
  if (!props.comment.trackedChange) return false;

  const context = {
    comment: props.comment,
    currentUser: proxy.$superdoc.config.user,
    superdoc: proxy.$superdoc,
  };

  if (isOwnComment.value) {
    return isAllowed(PERMISSIONS.REJECT_OWN, role, isInternal, context);
  }
  return isAllowed(PERMISSIONS.REJECT_OTHER, role, isInternal, context);
});

const allowOverflow = computed(() => {
  if (!generallyAllowed.value) return false;
  if (props.comment.trackedChange) return false;
  if (props.isPendingInput) return false;
  if (getOverflowOptions.value.length === 0) return false;

  return true;
});

const getOverflowOptions = computed(() => {
  if (!generallyAllowed.value) return false;

  const allowedOptions = [];
  const options = new Set();

  // Only the comment creator can edit, and only when comments aren't read-only
  if (!props.config.readOnly && isCommentOwnedByCurrentUser(props.comment)) {
    options.add('edit');
  }

  const isOwnComment = isCommentOwnedByCurrentUser(props.comment);

  const context = {
    comment: props.comment,
    currentUser: proxy.$superdoc.config.user,
    superdoc: proxy.$superdoc,
  };

  if (isOwnComment && isAllowed(PERMISSIONS.COMMENTS_DELETE_OWN, role, isInternal, context)) {
    options.add('delete');
  } else if (!isOwnComment && isAllowed(PERMISSIONS.COMMENTS_DELETE_OTHER, role, isInternal, context)) {
    options.add('delete');
  }

  options.forEach((option) => allowedOptions.push(OVERFLOW_OPTIONS[option]));
  return allowedOptions;
});

const handleResolve = () => {
  if (props.resolveDisabledReason) return;
  emit('resolve');
};
const handleReject = () => {
  if (props.rejectDisabledReason) return;
  emit('reject');
};
const handleSelect = (value) => emit('overflow-select', value);

// Imported comments have `origin` set (e.g. 'word'); imported tracked changes
// don't carry `origin` but do carry `importedAuthor` from the mark attributes.
// SD-2528: suppress the IMPORTED tag when the current user is the author —
// re-opening your own exported file shouldn't relabel your own comments as
// "imported"; that visual churn is what made round-tripping look broken.
const isImported = computed(() => {
  const hasImportOrigin = props.comment.origin != null || !!props.comment.importedAuthor?.name;
  if (!hasImportOrigin) return false;
  if (isCommentOwnedByCurrentUser(props.comment)) return false;
  return true;
});

const getCurrentUser = computed(() => {
  if (props.isPendingInput) return proxy.$superdoc.config.user;
  const user = props.comment.getCommentUser();
  // Strip "(imported)" qualifier from display name — the imported tag handles origin indication
  if (user?.name) {
    const cleaned = user.name.replace(/\s*\(imported\)\s*/gi, '').trim();
    if (cleaned) return { ...user, name: cleaned };
  }
  return user;
});
</script>

<template>
  <div class="card-section comment-header">
    <div class="comment-header-left">
      <Avatar :user="getCurrentUser" class="avatar" />
      <div class="user-info">
        <div class="user-name">
          {{ getCurrentUser.name }}<span v-if="isImported" class="imported-tag">IMPORTED</span>
        </div>
        <div class="user-timestamp" v-if="props.comment.createdTime">{{ formatDate(props.comment.createdTime) }}</div>
      </div>
    </div>

    <!-- Action buttons — visible on card hover and when active -->
    <div class="overflow-menu" :class="{ 'is-visible': props.isActive }">
      <div
        v-if="allowResolve"
        class="overflow-menu__icon"
        data-comment-action="resolve"
        :class="{ 'sd-is-disabled': Boolean(resolveDisabledReason) }"
        :data-disabled-reason="resolveDisabledReason || null"
        :aria-disabled="Boolean(resolveDisabledReason)"
        v-html="superdocIcons.markDone"
        @click.stop.prevent="handleResolve"
      ></div>

      <div
        v-if="allowReject"
        class="overflow-menu__icon"
        data-comment-action="reject"
        :class="{ 'sd-is-disabled': Boolean(rejectDisabledReason) }"
        :data-disabled-reason="rejectDisabledReason || null"
        :aria-disabled="Boolean(rejectDisabledReason)"
        v-html="superdocIcons.rejectChange"
        @click.stop.prevent="handleReject"
      ></div>

      <CommentsDropdown
        v-if="allowOverflow"
        :options="getOverflowOptions"
        @select="handleSelect"
        :content-style="{ fontFamily: uiFontFamily }"
      >
        <div class="overflow-menu__icon">
          <div class="overflow-icon" v-html="superdocIcons.overflow"></div>
        </div>
      </CommentsDropdown>
    </div>
  </div>
</template>

<style scoped>
.comment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.comment-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-info {
  display: flex;
  flex-direction: column;
}
.user-name {
  font-size: var(--sd-ui-comments-author-size, 14px);
  font-weight: var(--sd-ui-comments-author-weight, 600);
  color: var(--sd-ui-comments-author-text, #212121);
  line-height: 1.2em;
}
.imported-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--sd-ui-comments-tag-text, #888888);
  background: var(--sd-ui-comments-tag-bg, #f2f2f2);
  border-radius: 3px;
  padding: 1px 4px;
  margin-left: 6px;
  vertical-align: middle;
  line-height: 1.4;
}
.user-timestamp {
  line-height: 1.2em;
  font-size: var(--sd-ui-comments-timestamp-size, 12px);
  color: var(--sd-ui-comments-timestamp-text, #888888);
}
.overflow-menu {
  flex-shrink: 1;
  display: flex;
  gap: 6px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease;
}
.overflow-menu.is-visible {
  opacity: 1;
  pointer-events: auto;
}
.overflow-menu__icon {
  display: flex;
  box-sizing: content-box;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  padding: 3px;
  border-radius: 50%;
  color: var(--sd-ui-text, #47484a);
  cursor: pointer;
  transition: all 250ms ease;
}
.overflow-menu__icon:hover {
  background-color: var(--sd-ui-comments-separator, #dbdbdb);
}
.overflow-menu__icon.sd-is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: auto;
}
.overflow-menu__icon.sd-is-disabled:hover {
  background-color: transparent;
}
.overflow-menu__icon :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
  fill: currentColor;
}
.overflow-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 10px;
  height: 16px;
}
</style>
